/**
 * Hooks responsivos compartidos: escuchan media queries del navegador para
 * adaptar la UI (ficha del reporte, dashboards, cáscara) a pantallas angostas.
 */
import { useEffect, useState } from 'react';

/** Ancho máximo (px) que se considera pantalla angosta / móvil. */
export const NARROW_MAX = 720;

/** Media query equivalente a `useIsNarrow()`. */
export const NARROW_QUERY = `(max-width: ${NARROW_MAX}px)`;

/**
 * Unidad de alto de viewport: `dvh` (respeta la barra de direcciones móvil)
 * cuando el navegador la soporta; si no, `vh`.
 */
export const VH = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height', '100dvh')
    ? 'dvh'
    : 'vh';

const matches = (query) => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false
);

/**
 * Devuelve si la media query coincide y se actualiza cuando cambia.
 * @param {string} query Media query CSS, p. ej. `(max-width: 720px)`.
 * @returns {boolean}
 */
export function useMediaQuery(query) {
    const [isMatch, setIsMatch] = useState(() => matches(query));

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(query);
        const onChange = () => setIsMatch(mql.matches);
        onChange();
        if (mql.addEventListener) mql.addEventListener('change', onChange);
        else mql.addListener(onChange);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', onChange);
            else mql.removeListener(onChange);
        };
    }, [query]);

    return isMatch;
}

/**
 * Atajo: `true` cuando el viewport mide `NARROW_MAX` px o menos.
 * @returns {boolean}
 */
export function useIsNarrow() {
    return useMediaQuery(NARROW_QUERY);
}

/**
 * Lectura puntual (sin suscripción), útil para estados iniciales.
 * @returns {boolean}
 */
export function isNarrowViewport() {
    return matches(NARROW_QUERY);
}
